/**
 * Reportes oficiales del programa de la Dirección de Integración.
 *
 * El diseño NO se recrea: el Excel se genera sobre el archivo oficial
 * (public/plantillas/formato-listado-oficial.xlsx), que se carga con ExcelJS y se
 * rellena en su sitio. Así conserva tal cual la tipografía Times New Roman, los
 * colores de tema con tint, los bordes, los anchos de columna, las celdas
 * combinadas y el logo incrustado. Recrear todo eso a mano garantizaba
 * diferencias; partir del original garantiza que no las haya.
 *
 * Lo único que se toca son los valores y la cantidad de filas de cada bloque.
 *
 * Las cifras vienen calculadas del backend (cortes/{id}/reporte). Acá no se suma
 * nada: son los montos que se entregan a un tercero y salen de una sola fuente,
 * el corte congelado.
 */
import ExcelJS from 'exceljs'
import { generarPDF } from '../../mixins/reporteLib'

const PLANTILLA = '/plantillas/formato-listado-oficial.xlsx'
// Membrete para el PDF. Se dibuja a 380 pt de ancho; a 4800 px eso da unos
// 900 DPI, que aguanta el zoom sin verse borroso.
//
// Medido sobre el listado de Santa Ana, con la imagen registrada una sola vez en
// el diccionario del documento: 4800 px cuesta lo mismo que 1600 (234 ms) y solo
// 240 KB mas. Subir al original de 12344 px ya no compensa —1517 ms por apenas
// 113 KB mas— porque el costo pasa a ser decodificar 29 megapixeles.
//
// Si algun dia aparece el logo en vector, dejarlo como .svg en esta misma ruta:
// el codigo lo detecta por la extension y entonces es nitido a cualquier zoom y
// pesa unos pocos KB.
export const MEMBRETE = '/assets/LOGOOFICIAL_UNICAESCOLOR-pdf.png'
// La carta original va con la firma escaneada y el sello de la Dirección
// Administrativa. Esa imagen no se versiona acá: si se quiere que las cartas
// salgan firmadas, hay que dejarla en esta ruta y llamar a generarCartasPdf con
// conFirma = true. Si el archivo no está, la carta se genera sin firma.
export const FIRMA = '/assets/firma-integracion.jpg'

/**
 * Direcciones de las dos sedes para el pie de las cartas en PDF y en Word. El
 * listado oficial en Excel no lleva pie. Viven acá para que un cambio de
 * teléfono se haga una sola vez y las dos cartas no digan cosas distintas.
 */
export const SEDES_PIE = [
   {
      titulo: 'Sede Santa Ana',
      lineas: [
         'By Pass a Metapán y carretera antigua a San Salvador',
         'Santa Ana, El Salvador, C. A.',
         'Teléfono:(503) 2484-0600/(503) 2484-0602',
         'e-mail: catolica@catolica.edu.sv'
      ]
   },
   {
      titulo: 'Centro Regional de Ilobasco',
      lineas: [
         'Carretera a Ilobasco, Km. 56 Ctón Agua Zarca,',
         'Cabañas, El Salvador, C.A.',
         'Teléfono: (503) 2378-1500',
         'e-mail: Ilobasco@catolica.edu.sv'
      ]
   }
]

/**
 * Posiciones reales de la plantilla, hoja por hoja. Salieron de leer el XML del
 * archivo oficial; si alguien reordena filas en la plantilla hay que actualizar
 * este mapa.
 *
 * `filas` es cuántas filas de datos trae la plantilla en ese bloque: la
 * diferencia contra las que hagan falta se resuelve duplicando o quitando filas,
 * que es lo que mantiene los estilos.
 */
const HOJAS = [
   {
      nombre: 'POR COHORTE',
      ciclo: { fila: 9, ciclo: 'C', mesCompleto: 'D', fecha: 'F' },
      consolidado: {
         inicio: 14,
         filas: 5,
         total: 19,
         grupo: 'B',
         becados: 'C',
         detalle: 'D',
         totalBecados: 'C'
      },
      detalle: {
         encabezado: 23,
         colCuotaEncabezado: 'F',
         inicio: 24,
         filas: 443,
         total: 467,
         colTotal: 'F',
         unionTotal: ['A', 'E'],
         conSede: false,
         cols: ['A', 'B', 'C', 'D', 'E', 'F']
      },
      origen: 'porCohorte'
   },
   {
      nombre: 'POR SEDE',
      ciclo: { fila: 8, ciclo: 'C', mes: 'E', fecha: 'G' },
      consolidado: {
         inicio: 13,
         filas: 10,
         total: 23,
         grupo: 'B',
         becados: 'C',
         detalle: 'D',
         totalBecados: 'C'
      },
      detalle: {
         encabezado: 27,
         colCuotaEncabezado: 'G',
         inicio: 28,
         filas: 443,
         total: 471,
         colTotal: 'G',
         unionTotal: ['A', 'F'],
         conSede: true,
         cols: ['A', 'B', 'C', 'D', 'E', 'F', 'G']
      },
      origen: 'porSede'
   },
   {
      nombre: 'POR CARRERA-CUOTA DIFERENCIADA',
      ciclo: { fila: 9, ciclo: 'C', mes: 'E', fecha: 'G' },
      // Esta hoja lleva cuatro columnas en el consolidado: carrera, cuota,
      // cantidad y total, en vez de la frase "CUOTA DE $x".
      consolidado: {
         inicio: 14,
         filas: 28,
         total: 42,
         grupo: 'B',
         cuota: 'C',
         becados: 'D',
         importe: 'E',
         totalBecados: 'D',
         totalImporte: 'E',
         unionTotal: ['B', 'C']
      },
      detalle: {
         encabezado: 46,
         colCuotaEncabezado: 'G',
         inicio: 47,
         filas: 443,
         total: 490,
         colTotal: 'G',
         unionTotal: ['A', 'F'],
         conSede: true,
         cols: ['A', 'B', 'C', 'D', 'E', 'F', 'G']
      },
      origen: 'porCarrera'
   }
]

/** Hoja de laboratorios: misma plantilla, columnas propias. */
const HOJA_LABORATORIOS = {
   nombre: 'LABORATORIOS',
   ciclo: { fila: 9, ciclo: 'C', mes: 'E', fecha: 'G' },
   consolidado: {
      inicio: 14,
      filas: 2,
      total: 16,
      grupo: 'B',
      cuota: 'C',
      becados: 'D',
      importe: 'E',
      totalBecados: 'D',
      totalImporte: 'E'
   },
   detalle: {
      encabezado: 20,
      colCuotaEncabezado: 'J',
      inicio: 21,
      filas: 2,
      total: 23,
      unionTotal: ['A', 'F'],
      cols: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J']
   }
}

// ─── utilidades ──────────────────────────────────────────────────────────────

/**
 * Cronómetro simple para ver dónde se va el tiempo al generar los reportes.
 * Usa performance.now(), que da milisegundos con decimales, en vez de Date.
 */
export function cronometro() {
   const inicio = performance.now()
   let ultimo = inicio
   const etapas = []

   return {
      /** Cierra una etapa y guarda cuánto tardó desde la anterior. */
      etapa(nombre) {
         const ahora = performance.now()
         etapas.push([nombre, ahora - ultimo])
         ultimo = ahora
      },
      /** Vuelca las etapas y el total a la consola. */
      fin(titulo, extra) {
         const total = performance.now() - inicio
         const detalle = etapas
            .map(([n, ms]) => `${n}: ${ms.toFixed(0)} ms`)
            .join(' · ')

         console.log(
            `[INTEGRACION] ${titulo} en ${total.toFixed(0)} ms` +
               (extra ? ` (${extra})` : '') +
               (detalle
                  ? `
              ${detalle}`
                  : '')
         )

         return total
      }
   }
}

export function dinero(valor) {
   return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   }).format(Number(valor || 0))
}

/**
 * Deja el bloque con la cantidad de filas que hace falta.
 *
 * Duplicar en vez de crear filas nuevas es lo que conserva el formato: la copia
 * arrastra fuente, bordes, relleno y alineación de la fila modelo.
 * Devuelve cuántas filas se movieron, para recolocar todo lo que queda debajo.
 */
function ajustarBloque(ws, inicio, enPlantilla, necesarias) {
   const delta = necesarias - enPlantilla

   if (delta > 0) ws.duplicateRow(inicio, delta, true)
   else if (delta < 0) quitarFilas(ws, inicio, -delta)

   return delta
}

/**
 * Quita filas de verdad, que es más de lo que hace spliceRows.
 *
 * ExcelJS sube las filas que están DEBAJO del bloque que se quita y limpia el
 * lugar de donde salieron, pero no toca lo que queda en medio cuando el bloque
 * a quitar es más largo que esa cola. La plantilla trae 443 filas de detalle;
 * con 62 becarios hay que quitar 381 y abajo solo hay 63, así que 318 filas de
 * la plantilla —alumnos reales de Santa Ana— sobrevivían debajo de la fila de
 * TOTALES y salían impresas en el reporte.
 *
 * La cuenta sale sola: spliceRows solo deja la hoja limpia mientras lo que se
 * quita no supere a lo que queda debajo, o sea la mitad del tramo. Se quita en
 * tandas de ese tamaño, que con 381 filas son tres llamadas.
 */
function quitarFilas(ws, inicio, cuantas) {
   let quedan = cuantas

   while (quedan > 0) {
      const tramo = ws.rowCount - inicio + 1
      const tanda = Math.max(1, Math.min(quedan, Math.floor(tramo / 2)))

      ws.spliceRows(inicio, tanda)
      quedan -= tanda
   }
}

/**
 * Vuelve a combinar las celdas de una fila y deja el valor solo en la primera.
 *
 * ExcelJS pierde las combinaciones de las filas que se mueven al insertar o
 * quitar filas, y al perderlas vuelca el valor de la celda maestra en cada una
 * de las que estaban unidas: por eso "TOTALES" salía repetido columna por
 * columna. Hay que desunir antes de limpiar, porque escribir en una celda
 * todavía unida escribe en realidad sobre la maestra.
 */
function rehacerUnion(ws, fila, columnas) {
   if (!columnas) return

   const rango = `${columnas[0]}${fila}:${columnas[1]}${fila}`
   const valor = ws.getCell(`${columnas[0]}${fila}`).value

   try {
      ws.unMergeCells(rango)
   } catch (e) {
      // Ya venía desunida: es justamente el caso que se está arreglando.
   }

   const desde = ws.getColumn(columnas[0]).number
   const hasta = ws.getColumn(columnas[1]).number
   for (let c = desde; c <= hasta; c++) ws.getCell(fila, c).value = null

   ws.mergeCells(rango)
   ws.getCell(`${columnas[0]}${fila}`).value = valor
}

function escribir(ws, columna, fila, valor) {
   if (columna) ws.getCell(`${columna}${fila}`).value = valor
}

/**
 * Ciclo que se imprime en la cabecera. Cuando el mes se cargó con un ciclo
 * adicional —junio y diciembre caen entre dos ciclos— las cuotas salen de los
 * dos, así que el reporte tiene que decirlo: "II-2026 y I-2026".
 */
function etiquetaCiclo(reporte) {
   if (!reporte.cicloAlterno) return reporte.ciclo

   return `${reporte.ciclo} y ${reporte.cicloAlterno}`
}

/** Fecha real para que respete el formato de fecha que ya trae la celda. */
function fechaDelCorte(reporte) {
   const partes = (reporte.fechaCorte || '').split('/')
   if (partes.length !== 3) return new Date()

   return new Date(Number(partes[2]), Number(partes[1]) - 1, Number(partes[0]))
}

// ─── llenado de una hoja ─────────────────────────────────────────────────────

function llenarHoja(wb, mapa, reporte, consolidado, detalle) {
   const ws = wb.getWorksheet(mapa.nombre)
   if (!ws) return

   // Primero el bloque de abajo: así los índices del de arriba siguen valiendo
   // mientras se hace el corte, y después se corrigen todos de una vez.
   const dDetalle = ajustarBloque(
      ws,
      mapa.detalle.inicio,
      mapa.detalle.filas,
      Math.max(detalle.length, 1)
   )
   const dConsolidado = ajustarBloque(
      ws,
      mapa.consolidado.inicio,
      mapa.consolidado.filas,
      Math.max(consolidado.length, 1)
   )

   // Cabecera: ciclo, mes y fecha.
   const c = mapa.ciclo
   const ciclo = etiquetaCiclo(reporte)
   escribir(ws, c.ciclo, c.fila, ciclo)

   // La celda del ciclo queda pegada a la del mes, así que con dos ciclos el
   // texto no cabe y Excel lo recorta. Se ensancha la columna solo en ese caso:
   // con un ciclo la plantilla queda intacta.
   if (reporte.cicloAlterno) {
      const columna = ws.getColumn(c.ciclo)
      columna.width = Math.max(columna.width || 0, ciclo.length + 2)
   }
   if (c.mesCompleto) {
      escribir(
         ws,
         c.mesCompleto,
         c.fila,
         `MES: ${reporte.mesNombre} ${reporte.anio}`
      )
   } else {
      escribir(ws, c.mes, c.fila, reporte.mesNombre)
   }
   escribir(ws, c.fecha, c.fila, fechaDelCorte(reporte))

   // Consolidado.
   const co = mapa.consolidado
   consolidado.forEach((fila, i) => {
      const r = co.inicio + i
      escribir(ws, co.grupo, r, fila.grupo)

      if (co.cuota) {
         // Hoja "por carrera": cuota, cantidad e importe en columnas propias.
         escribir(ws, co.cuota, r, Number(fila.cuota))
         escribir(ws, co.becados, r, fila.becados)
         escribir(ws, co.importe, r, Number(fila.total))
      } else {
         escribir(ws, co.becados, r, fila.becados)
         escribir(ws, co.detalle, r, `CUOTA DE $${dinero(fila.cuota)}`)
      }
   })

   const filaTotalConsolidado = co.total + dConsolidado
   escribir(
      ws,
      co.totalBecados,
      filaTotalConsolidado,
      consolidado.reduce((a, f) => a + f.becados, 0)
   )
   if (co.totalImporte) {
      escribir(
         ws,
         co.totalImporte,
         filaTotalConsolidado,
         consolidado.reduce((a, f) => a + Number(f.total), 0)
      )
   }

   // Detalle. Todo lo que va debajo del consolidado se corrió dConsolidado filas.
   const de = mapa.detalle
   const filaEncabezado = de.encabezado + dConsolidado
   escribir(
      ws,
      de.colCuotaEncabezado,
      filaEncabezado,
      `CUOTA ${reporte.mesNombre}`
   )

   const inicioDetalle = de.inicio + dConsolidado
   detalle.forEach((valores, i) => {
      valores.forEach((valor, j) => {
         escribir(ws, de.cols[j], inicioDetalle + i, valor)
      })
   })

   escribir(
      ws,
      de.colTotal,
      de.total + dDetalle + dConsolidado,
      Number(reporte.totalMonto)
   )

   // Las filas de TOTALES vienen con celdas combinadas en la plantilla y la
   // cirugía de filas las desarma, así que se rehacen ya en su posición final.
   rehacerUnion(ws, filaTotalConsolidado, co.unionTotal)
   rehacerUnion(ws, de.total + dDetalle + dConsolidado, de.unionTotal)

   // El filtro viene anclado en la plantilla a las filas que ella traía. Como
   // los bloques crecen y se achican según el mes, hay que volver a anclarlo al
   // encabezado real del detalle: si no, la flecha del filtro queda sobre una
   // fila de alumno y la tabla aparece partida por la mitad.
   const ultimaFila = inicioDetalle + Math.max(detalle.length, 1) - 1
   const primeraCol = de.cols[0]
   const ultimaCol = de.cols[de.cols.length - 1]
   ws.autoFilter = `${primeraCol}${filaEncabezado}:${ultimaCol}${ultimaFila}`
}

// ─── Excel oficial ───────────────────────────────────────────────────────────

function filasDetalle(reporte, conSede) {
   return reporte.detalle.map((d) => {
      const fila = [d.no, d.nombreCompleto, d.carnet, d.carreraNombre]
      if (conSede) fila.push(d.sede)
      fila.push(d.cohorte, Number(d.montoIntegracion))
      return fila
   })
}

export async function generarExcelOficial(reporte) {
   const reloj = cronometro()

   const respuesta = await fetch(PLANTILLA)
   if (!respuesta.ok) {
      throw new Error(
         'No se encontró la plantilla del listado oficial en /plantillas.'
      )
   }
   reloj.etapa('descarga plantilla')

   const wb = new ExcelJS.Workbook()
   await wb.xlsx.load(await respuesta.arrayBuffer())
   reloj.etapa('lectura plantilla')

   HOJAS.forEach((mapa) => {
      llenarHoja(
         wb,
         mapa,
         reporte,
         reporte[mapa.origen] || [],
         filasDetalle(reporte, mapa.detalle.conSede)
      )
   })

   // Laboratorios: la estructura se conserva aunque hoy no haya datos, porque es
   // la hoja que espera el formato oficial.
   const conLaboratorio = reporte.detalle.filter(
      (d) => d.cantidadLaboratorios > 0
   )
   llenarHoja(
      wb,
      HOJA_LABORATORIOS,
      reporte,
      [],
      conLaboratorio.map((d, i) => [
         i + 1,
         d.nombreCompleto,
         d.carnet,
         d.carreraNombre,
         d.sede,
         d.cohorte,
         d.cantidadLaboratorios,
         Number(d.cuotaLaboratorio),
         Number(d.totalLaboratorios),
         Number(d.montoIntegracion)
      ])
   )

   reloj.etapa('llenado de hojas')

   const buffer = await wb.xlsx.writeBuffer()
   reloj.etapa('escritura xlsx')

   const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
   })
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.href = url
   a.download = `LISTADO OFICIAL IES_CFP CICLO ${reporte.ciclo} ${reporte.mesNombre}.xlsx`
   a.click()
   window.URL.revokeObjectURL(url)

   reloj.fin(
      'Excel oficial generado',
      `${reporte.detalle.length} becarios · ${(
         buffer.byteLength / 1024
      ).toFixed(0)} KB`
   )
}

// ─── Reporte de bajas ────────────────────────────────────────────────────────

/**
 * Columnas del reporte de bajas, en el orden en que se leen.
 *
 * El motivo y la fecha van primero de lo que no identifica al alumno: es lo que
 * se busca al abrir este archivo. El correo y el teléfono se incluyen porque la
 * baja suele terminar en una llamada o un correo a la persona.
 */
const COLUMNAS_BAJAS = [
   { titulo: 'N°', ancho: 5, valor: (b, i) => i + 1 },
   { titulo: 'CARNET', ancho: 14, valor: (b) => b.carnet },
   { titulo: 'NOMBRE COMPLETO', ancho: 38, valor: (b) => b.nombreCompleto },
   { titulo: 'CARRERA', ancho: 40, valor: (b) => b.carreraNombre },
   { titulo: 'FACULTAD', ancho: 11, valor: (b) => b.facultadCodigo },
   { titulo: 'SEDE', ancho: 12, valor: (b) => b.sede },
   { titulo: 'PROMOCIÓN', ancho: 11, valor: (b) => b.cohorte },
   { titulo: 'MOTIVO DE LA BAJA', ancho: 28, valor: (b) => b.motivoRetiro },
   { titulo: 'FECHA DE RETIRO', ancho: 16, valor: (b) => b.fechaRetiro },
   { titulo: 'OBSERVACIÓN', ancho: 42, valor: (b) => b.observacion },
   {
      titulo: 'CORREO INSTITUCIONAL',
      ancho: 34,
      valor: (b) => b.correoInstitucional
   },
   { titulo: 'TELÉFONO', ancho: 13, valor: (b) => b.telefono }
]

/**
 * Reporte de los becarios dados de baja en un mes.
 *
 * Sale de lo que la pantalla tiene en ese momento, con los filtros puestos: lo
 * que se ve es lo que se exporta. Por eso el encabezado deja escrito de qué mes
 * y con qué filtros se sacó — un archivo suelto sin esa referencia no se puede
 * interpretar después.
 *
 * `contexto` lleva ciclo, mes, anio y los filtros aplicados.
 */
export async function generarExcelBajas(bajas, contexto) {
   const reloj = cronometro()

   const wb = new ExcelJS.Workbook()
   wb.creator = 'Colecturía Digital'
   wb.created = new Date()

   const ws = wb.addWorksheet('Bajas', {
      views: [{ state: 'frozen', ySplit: 4 }],
      pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 }
   })

   ws.columns = COLUMNAS_BAJAS.map((c) => ({ width: c.ancho }))

   const ultima = COLUMNAS_BAJAS.length

   const titulo = ws.getCell('A1')
   titulo.value = 'BECARIOS DADOS DE BAJA'
   titulo.font = { name: 'Calibri', size: 14, bold: true }
   ws.mergeCells(1, 1, 1, ultima)

   const subtitulo = ws.getCell('A2')
   subtitulo.value =
      `Ciclo ${contexto.ciclo || '—'} · ${contexto.mesNombre || ''} ${
         contexto.anio || ''
      }`.trim() + ` · ${bajas.length} baja(s)`
   subtitulo.font = { name: 'Calibri', size: 11, color: { argb: 'FF595959' } }
   ws.mergeCells(2, 1, 2, ultima)

   // Los filtros que estaban puestos. Sin esto, dos archivos del mismo mes con
   // distinta cantidad de filas no se pueden explicar.
   const filtros = []
   if (contexto.sede) filtros.push(`Sede: ${contexto.sede}`)
   if (contexto.carrera) filtros.push(`Carrera: ${contexto.carrera}`)
   if (contexto.busqueda) filtros.push(`Búsqueda: "${contexto.busqueda}"`)

   const pie = ws.getCell('A3')
   pie.value = filtros.length
      ? `Filtros aplicados — ${filtros.join(' · ')}`
      : 'Sin filtros: todas las bajas del mes.'
   pie.font = {
      name: 'Calibri',
      size: 9,
      italic: true,
      color: { argb: 'FF808080' }
   }
   ws.mergeCells(3, 1, 3, ultima)

   const encabezado = ws.getRow(4)
   COLUMNAS_BAJAS.forEach((c, i) => {
      const celda = encabezado.getCell(i + 1)
      celda.value = c.titulo
      celda.font = {
         name: 'Calibri',
         size: 10,
         bold: true,
         color: { argb: 'FFFFFFFF' }
      }
      celda.fill = {
         type: 'pattern',
         pattern: 'solid',
         fgColor: { argb: 'FF44546A' }
      }
      celda.alignment = {
         vertical: 'middle',
         horizontal: 'center',
         wrapText: true
      }
   })
   encabezado.height = 28

   bajas.forEach((baja, i) => {
      const fila = ws.getRow(5 + i)

      COLUMNAS_BAJAS.forEach((c, j) => {
         const celda = fila.getCell(j + 1)
         const valor = c.valor(baja, i)

         celda.value =
            valor === undefined || valor === null || valor === '' ? '—' : valor
         celda.font = { name: 'Calibri', size: 10 }
         celda.alignment = { vertical: 'top', wrapText: true }
      })
   })

   // Bordes finos en toda la rejilla, encabezado incluido.
   const borde = { style: 'thin', color: { argb: 'FFBFBFBF' } }
   for (let f = 4; f <= 4 + bajas.length; f++) {
      for (let c = 1; c <= ultima; c++) {
         ws.getCell(f, c).border = {
            top: borde,
            left: borde,
            bottom: borde,
            right: borde
         }
      }
   }

   // Autofiltro sobre el encabezado: el archivo se suele repasar por motivo.
   if (bajas.length) {
      ws.autoFilter = {
         from: { row: 4, column: 1 },
         to: { row: 4 + bajas.length, column: ultima }
      }
   }

   reloj.etapa('armado de la hoja')

   const buffer = await wb.xlsx.writeBuffer()
   const nombre = `BAJAS INTEGRACION ${contexto.mesNombre || ''} ${
      contexto.anio || ''
   }`
      .replace(/\s+/g, ' ')
      .trim()

   const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
   })
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.href = url
   a.download = `${nombre}.xlsx`
   a.click()
   window.URL.revokeObjectURL(url)

   reloj.fin('Reporte de bajas generado', `${bajas.length} baja(s)`)
}

// ─── Cartas en PDF ───────────────────────────────────────────────────────────

/**
 * Todas las medidas de acá salieron de medir el PDF oficial de agosto 2026
 * (posiciones de texto, matrices de las imágenes, rectángulos de las tablas y
 * operadores de color del propio archivo). No son estimaciones: si algo se
 * cambia, conviene volver a medir contra ese PDF.
 *
 * Página LETTER vertical, 612 x 792 pt.
 */
export const CARTA = {
   // Márgenes de página: los fija la tabla, que va de x=42.8 a x=575.3.
   margenIzq: 42.8,
   margenDer: 36.7,
   margenSup: 114,
   margenInf: 78,

   // El cuerpo de la carta NO comparte margen con las tablas: el texto arranca
   // en x=56.6 y termina en ~555, mientras la tabla se sale a 42.8 y 575.3. Esa
   // diferencia de 13.8 pt es la sangría que se ve en el documento oficial.
   sangriaCuerpoIzq: 13.8,
   sangriaCuerpoDer: 20.3,

   // Membrete: en el original mide 72.5 pt de alto. Se fija el ancho para que el
   // logo a color, que es un poco menos apaisado, conserve esa misma altura.
   membreteAncho: 380,
   membreteMargenSup: 22.7,

   // Firma escaneada, centrada al pie de la última página.
   firmaAncho: 210.2,

   // Espacio en blanco entre el final del resumen y el nombre de quien firma,
   // para el sello y la firma de puño. Medido en el original de Santa Ana: la
   // fila TOTALES cierra en y=347.3 y el nombre arranca en y=204.2.
   espacioFirma: 143,

   // Relleno de los encabezados de tabla: 0.675 0.725 0.792 rg en el original.
   rellenoEncabezado: '#ACB9CA',
   // Negro puro en todo el texto y las líneas de tabla. El original traía un
   // gris (#3B3838) y la Dirección pidió letra negra en el PDF y en el Word.
   colorTexto: '#000000',
   lineaTabla: 0.4,
   padCeldaH: 2,
   padCeldaV: 1.5,
   // El encabezado del listado mide 36.6 pt en el original. Los tres renglones
   // de 8 pt ocupan 22 pt medidos, así que el relleno es (36.6 - 22) / 2.
   padEncabezadoV: 7.3,

   // Anchos exactos de las columnas del listado nominal.
   colsListado: [17.8, 59.4, 132.2, 134.4, 56.8, 57.0, 73.4],
   // Anchos exactos del resumen por carreras.
   colsResumen: [21.1, 198.2, 63.4, 104.5, 79.0, 66.3],

   // Quién firma. Está en el documento oficial; si cambia la persona se cambia acá.
   firmante: 'Mtra. Ana Michelle Espinoza',
   cargoFirmante: 'Directora Administrativa'
}

function esVector(url) {
   return /\.svg(\?|$)/i.test(url || '')
}

/**
 * Carga el membrete. Un SVG se trae como texto —pdfmake lo dibuja como vector y
 * queda nitido a cualquier zoom— y cualquier otro formato como data URL.
 */
async function cargarMembrete(url) {
   if (!esVector(url)) return toDataURL(url)

   try {
      const respuesta = await fetch(url)
      if (!respuesta.ok) return null
      return await respuesta.text()
   } catch (e) {
      return null
   }
}

/** Carga una imagen del sitio como data URL, igual que ComprobantesPago. */
function toDataURL(url) {
   return new Promise(function (resolve) {
      const xhr = new XMLHttpRequest()
      xhr.onload = function () {
         const reader = new FileReader()
         reader.onloadend = function () {
            resolve(reader.result)
         }
         reader.readAsDataURL(xhr.response)
      }
      xhr.onerror = function () {
         resolve(null)
      }
      xhr.open('GET', url)
      xhr.responseType = 'blob'
      xhr.send()
   })
}

const MESES_TEXTO = [
   '',
   'enero',
   'febrero',
   'marzo',
   'abril',
   'mayo',
   'junio',
   'julio',
   'agosto',
   'septiembre',
   'octubre',
   'noviembre',
   'diciembre'
]

/** "2 de septiembre del 2026", como en la carta original. */
export function fechaLarga(reporte) {
   const f = fechaDelCorte(reporte)
   return `${f.getDate()} de ${
      MESES_TEXTO[f.getMonth() + 1]
   } del ${f.getFullYear()}`
}

/**
 * Celda de dinero al estilo contable del original: el signo pegado a la
 * izquierda de la celda y el importe alineado a la derecha, no "$80.00" junto.
 */
function celdaMonto(valor, tam, negrita) {
   return {
      columns: [
         { text: '$', width: 'auto', bold: !!negrita },
         {
            text: dinero(valor),
            width: '*',
            alignment: 'right',
            bold: !!negrita
         }
      ],
      fontSize: tam,
      columnGap: 2
   }
}

/**
 * Bordes finos en toda la rejilla, del grosor del documento original.
 */
const rejilla = {
   hLineWidth: () => CARTA.lineaTabla,
   vLineWidth: () => CARTA.lineaTabla,
   hLineColor: () => CARTA.colorTexto,
   vLineColor: () => CARTA.colorTexto,
   paddingTop: (i) => (i === 0 ? CARTA.padEncabezadoV : CARTA.padCeldaV),
   paddingBottom: (i) => (i === 0 ? CARTA.padEncabezadoV : CARTA.padCeldaV),
   paddingLeft: () => CARTA.padCeldaH,
   paddingRight: () => CARTA.padCeldaH
}

/**
 * pdfmake interpreta `widths` como el ancho del CONTENIDO de la celda: al
 * dibujar le suma el padding horizontal y el grosor de la línea. Declarar los
 * anchos medidos en el original dejaba cada columna 4.4 pt más ancha y la tabla
 * se salía 29 pt por la derecha. Acá se descuenta para que el ancho final sea
 * exactamente el del documento oficial.
 */
function anchosReales(columnas) {
   const extra = CARTA.padCeldaH * 2 + CARTA.lineaTabla

   return columnas.map((ancho) => Number((ancho - extra).toFixed(2)))
}

/** Todo el contenido de las tablas del documento oficial va en mayúsculas. */
export function may(texto) {
   return (texto || '').toString().toUpperCase()
}

function th(texto, tam) {
   return {
      text: texto,
      bold: true,
      fontSize: tam,
      alignment: 'center',
      fillColor: CARTA.rellenoEncabezado
   }
}

/** Listado nominal: una fila por becario. */
function tablaListado(reporte, sede, porcentaje) {
   const cuerpo = [
      [
         th('N°', 8),
         th('CARNET', 8),
         th('NOMBRE', 8),
         th('CARRERA', 8),
         th('FACULTAD', 8),
         th('SEDE', 8),
         // Tres renglones explícitos, como en el original. Dejado al ajuste
         // automático entra en dos, y un salto de línea dentro del texto de la
         // celda tampoco los separa.
         {
            stack: [
               { text: 'MONTO' },
               { text: 'INTEGRACIÓN' },
               { text: porcentaje }
            ],
            bold: true,
            fontSize: 8,
            alignment: 'center',
            fillColor: CARTA.rellenoEncabezado
         }
      ]
   ]

   sede.detalle.forEach((d) => {
      cuerpo.push([
         { text: String(d.no), fontSize: 8, alignment: 'center' },
         { text: may(d.carnet), fontSize: 8, alignment: 'center' },
         { text: may(d.nombreCompleto), fontSize: 8 },
         // La carrera va un punto más chica: es la columna que más se desborda.
         { text: may(d.carreraNombre), fontSize: 7, alignment: 'center' },
         { text: may(d.facultadCodigo), fontSize: 8, alignment: 'center' },
         { text: may(d.sede), fontSize: 8, alignment: 'center' },
         celdaMonto(d.montoIntegracion, 8)
      ])
   })

   cuerpo.push([
      { text: '', fillColor: CARTA.rellenoEncabezado },
      { text: '', fillColor: CARTA.rellenoEncabezado },
      { text: '', fillColor: CARTA.rellenoEncabezado },
      {
         text: 'TOTALES',
         bold: true,
         fontSize: 8,
         fillColor: CARTA.rellenoEncabezado
      },
      { text: '', fillColor: CARTA.rellenoEncabezado },
      { text: '', fillColor: CARTA.rellenoEncabezado },
      { ...celdaMonto(sede.total, 8, true), fillColor: CARTA.rellenoEncabezado }
   ])

   return {
      table: {
         // El encabezado se repite al inicio de cada pagina: cuando el listado
         // no cabe en una, las filas que seguian quedaban sin titulo de
         // columna y no se distinguia que dato era cada uno. keepWithHeaderRows
         // evita que el encabezado quede solo al pie de una pagina.
         headerRows: 1,
         keepWithHeaderRows: 1,
         dontBreakRows: true,
         widths: anchosReales(CARTA.colsListado),
         body: cuerpo
      },
      layout: rejilla
   }
}

/** Resumen por carreras, con la cuota completa y la parte que aporta la D.I. */
function tablaResumen(sede, porcentaje, centro) {
   const cuerpo = [
      [
         th('N°', 9),
         th(`CARRERAS UNICAES ${centro.toUpperCase()}`, 9),
         th('N° DE ALUMNOS', 9),
         th('VALOR CUOTA UNICAES', 9),
         th(`VALOR CUOTA ${porcentaje} (D.I)`, 9),
         th('MONTO', 9)
      ]
   ]

   sede.carreras.forEach((c, i) => {
      cuerpo.push([
         { text: String(i + 1), fontSize: 9, alignment: 'center' },
         { text: may(c.carrera), fontSize: 9 },
         { text: String(c.becados), fontSize: 9, alignment: 'center' },
         celdaMonto(c.cuotaUnicaes, 9),
         celdaMonto(c.cuotaIntegracion, 9),
         celdaMonto(c.monto, 9)
      ])
   })

   cuerpo.push([
      { text: '', fillColor: CARTA.rellenoEncabezado },
      {
         text: 'TOTALES',
         bold: true,
         fontSize: 9,
         alignment: 'center',
         fillColor: CARTA.rellenoEncabezado
      },
      {
         text: String(sede.becados),
         bold: true,
         fontSize: 9,
         alignment: 'center',
         fillColor: CARTA.rellenoEncabezado
      },
      { text: '', fillColor: CARTA.rellenoEncabezado },
      { text: '', fillColor: CARTA.rellenoEncabezado },
      { ...celdaMonto(sede.total, 9, true), fillColor: CARTA.rellenoEncabezado }
   ])

   return {
      table: {
         // Igual que el listado: el encabezado se repite en cada pagina.
         headerRows: 1,
         keepWithHeaderRows: 1,
         widths: anchosReales(CARTA.colsResumen),
         body: cuerpo
      },
      layout: rejilla
   }
}

/**
 * Pie con las direcciones de las dos sedes, en dos columnas centradas.
 * En el original aparece solo en las páginas finales; acá va en todas porque su
 * ausencia en las primeras parece un descuido del documento, no una decisión.
 */
function pieDePagina() {
   const columna = (titulo, lineas) => ({
      width: '*',
      alignment: 'center',
      stack: [
         { text: titulo, bold: true, fontSize: 11 },
         ...lineas.map((t) => ({ text: t, fontSize: 9 }))
      ]
   })

   return {
      columns: SEDES_PIE.map((s) => columna(s.titulo, s.lineas)),
      margin: [CARTA.margenIzq, 8, CARTA.margenDer, 0],
      color: CARTA.colorTexto
   }
}

/**
 * Aplica la sangría del cuerpo de la carta a un elemento, sumándola al margen
 * que ya traiga. Las tablas no pasan por acá: van al ancho completo.
 */
function cuerpo(elemento) {
   const m = elemento.margin || [0, 0, 0, 0]

   return {
      ...elemento,
      margin: [
         CARTA.sangriaCuerpoIzq + m[0],
         m[1],
         CARTA.sangriaCuerpoDer + m[2],
         m[3]
      ]
   }
}

/**
 * Carta oficial de una sede, calcada del documento que hoy se envía a mano.
 */
function documentoCarta(reporte, sede, membrete, firma) {
   const membreteEsVector = esVector(MEMBRETE) && !!membrete
   const esIlobasco = sede.sede === 'ILOBASCO'
   const lugar = esIlobasco ? 'Ilobasco' : 'Santa Ana'
   const centro = esIlobasco ? 'Centro Regional de Ilobasco' : 'Sede Santa Ana'
   const porcentaje = `${Number(reporte.porcentajeIntegracion)}%`
   const promocion = sede.detalle.length ? sede.detalle[0].cohorte : ''

   return {
      pageSize: 'LETTER',
      pageOrientation: 'portrait',
      pageMargins: [
         CARTA.margenIzq,
         CARTA.margenSup,
         CARTA.margenDer,
         CARTA.margenInf
      ],
      defaultStyle: {
         font: 'TimesNewRoman',
         fontSize: 12,
         color: CARTA.colorTexto
      },

      // El membrete va en el diccionario de imágenes y el encabezado lo llama
      // por nombre. Pasarlo como data URL dentro del header lo incrustaba otra
      // vez por cada página: catorce copias de medio mega, 6.4 MB de archivo y
      // catorce segundos de armado.
      // Un SVG se dibuja en linea, no se registra como imagen.
      images: Object.assign(
         {},
         membrete && !membreteEsVector ? { membrete } : null,
         firma ? { firma } : null
      ),

      // El membrete se repite en todas las páginas, como en el original.
      header: () => ({
         stack: [
            membreteEsVector
               ? {
                    svg: membrete,
                    width: CARTA.membreteAncho,
                    alignment: 'center'
                 }
               : membrete
               ? {
                    image: 'membrete',
                    width: CARTA.membreteAncho,
                    alignment: 'center'
                 }
               : { text: '' },
            {
               text: 'http://www.catolica.edu.sv',
               fontSize: 11,
               alignment: 'center',
               margin: [0, 4, 0, 0],
               color: CARTA.colorTexto
            }
         ],
         margin: [0, CARTA.membreteMargenSup, 0, 0]
      }),
      footer: pieDePagina,

      content: [
         // El bloque de la carta va sangrado respecto de las tablas: en el
         // documento oficial arranca en x=56.6 y cierra en ~555, mientras que las
         // tablas se abren hasta 42.8 y 575.3. Esa diferencia es el tab que se ve
         // encima del título.
         cuerpo({
            text: `${lugar}, ${fechaLarga(reporte)}`,
            alignment: 'right'
         }),
         cuerpo({ text: 'Señores', margin: [0, 1.8, 0, 0] }),
         cuerpo({ text: 'Dirección de Integración', bold: true }),
         cuerpo({ text: 'Presente' }),
         cuerpo({ text: 'Estimados Señores:', margin: [0, 13.9, 0, 0] }),
         cuerpo({
            text:
               `Con base al convenio, remito informe correspondiente al mes de ` +
               `${reporte.mesNombre.toLowerCase()} en el ciclo ${
                  reporte.ciclo
               } de becados de la ` +
               `Universidad Católica de El Salvador, ${centro}.`,
            alignment: 'justify',
            margin: [0, 13.8, 0, 0]
         }),
         {
            text: 'PROGRAMA DE BECAS DIRECCIÓN DE INTEGRACIÓN',
            bold: true,
            fontSize: 10,
            alignment: 'center',
            margin: [0, 13.9, 0, 0]
         },
         {
            text: `PROMOCIÓN ${promocion}`,
            bold: true,
            fontSize: 10,
            alignment: 'center',
            margin: [0, 1.6, 0, 3]
         },

         tablaListado(reporte, sede, porcentaje),

         {
            text: `Resumen por carreras ${centro}`,
            bold: true,
            fontSize: 12,
            pageBreak: 'before',
            margin: [42, 0, 0, 16]
         },
         tablaResumen(sede, porcentaje, centro),

         {
            // El espacio para sellar y firmar va como una fila de tabla vacia de
            // alto fijo, no como margen: pdfmake descarta el margen superior
            // cuando el bloque cae al inicio de una pagina, y ahi el nombre
            // quedaba pegado al membrete sin lugar para la firma.
            //
            // Todo junto y sin cortes, para que el hueco y el nombre no terminen
            // en paginas distintas.
            unbreakable: true,
            stack: [
               {
                  table: {
                     widths: ['*'],
                     heights: [
                        firma ? CARTA.espacioFirma - 40 : CARTA.espacioFirma
                     ],
                     body: [
                        [{ text: '', border: [false, false, false, false] }]
                     ]
                  },
                  layout: 'noBorders'
               },
               ...(firma
                  ? [
                       {
                          image: 'firma',
                          width: CARTA.firmaAncho,
                          alignment: 'center'
                       }
                    ]
                  : []),
               {
                  text: CARTA.firmante,
                  fontSize: 10,
                  alignment: 'center',
                  margin: [0, firma ? -12 : 0, 0, 0]
               },
               {
                  text: CARTA.cargoFirmante,
                  fontSize: 10,
                  alignment: 'center'
               }
            ]
         }
      ]
   }
}

/**
 * Una carta por sede: así se entregan, cada sede remite la suya.
 *
 * `conFirma` decide si se estampa la firma escaneada de la Dirección
 * Administrativa. Va apagado por defecto a propósito: que un botón emita cartas
 * ya firmadas es una decisión de la universidad, no del reporte. Además la
 * imagen no viene en el repositorio (ver FIRMA).
 */
export async function generarCartasPdf(reporte, conFirma = false) {
   const reloj = cronometro()

   const membrete = await cargarMembrete(MEMBRETE)
   const firma = conFirma ? await toDataURL(FIRMA) : null
   reloj.etapa('carga de imágenes')

   reporte.resumenPorSede.forEach((sede) => {
      const nombre = `BECARIOS UNICAES ${sede.sede} ${reporte.mesNombre}.pdf`
      const doc = documentoCarta(reporte, sede, membrete, firma)
      reloj.etapa(`armado ${sede.sede}`)

      // pdfmake arma y descarga el PDF de forma asíncrona: lo que se mide acá es
      // el trabajo de esta pestaña hasta soltarlo, no lo que tarda el navegador
      // en escribir el archivo.
      generarPDF('save', doc, nombre)
      reloj.etapa(`render ${sede.sede} (${sede.detalle.length} filas)`)
   })

   reloj.fin(
      'Cartas PDF generadas',
      `${reporte.resumenPorSede.length} sede(s) · ${reporte.detalle.length} becarios`
   )
}
